import { React, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Buffer } from 'buffer';

import Spinner from './../spinner/Spinner';

import { SubscriberDetails } from '../../models/SubscriberDetails';
import { ListMonthlyDownloadDateResult } from '../../models/ListMonthlyDownloadDateResult';
import { MonthlyDownloadDate } from '../../models/MonthlyDownloadDate';
import { PackageDetails } from '../../models/PackageDetails';
import { Packages } from '../../models/Packages';

import config from '../../config/config.json';

import './Login.scss';

function Login(props) {

  const [subId, setSubId] = useState('');
  const [password, setPassword] = useState('');
  const [loginMessage, setLoginMessage] = useState();

  const [userAuthenticated, setUserAuthenticated] = useState(false);
  const [subscriberDetails, setSubscriberDetails] = useState(new SubscriberDetails());
  const [guid, setGuid] = useState();
  const [packages, setPackages] = useState(new Packages());
  const [monthlyDownloadDates, setMonthlyDownloadDates] = useState(new ListMonthlyDownloadDateResult());
  const [lastLogin, setLastLogin] = useState();
  const [dataLoading, setDataLoading] = useState(false); 

  const navigate = useNavigate();

  
  useEffect(() => {
    if (localStorage.getItem('USER_AUTHENTICATED') === 'true') {
      localStorage.removeItem('USER_AUTHENTICATED');
      localStorage.removeItem('SUBSCRIBER_DETAILS');
      localStorage.removeItem('GUID');
      localStorage.removeItem('PACKAGES');
      localStorage.removeItem('MONTHLY_DOWNLOAD_DATES');
      localStorage.removeItem('LAST_LOGIN');
      props.user({
        userAuthenticated: false,
        subscriberDetails: new SubscriberDetails(),
        guid: null,
        packages: null,
        monthlyDownloadDates: null,
        lastLogin: null,
        dataLoading: false
      });
    }
  
  }, []);

  // API request
  async function loginSubscriber(subId, password) {
    setDataLoading(true);
    try {

      const baseUrl = props.env.USE_LOCAL === "true" ? config.apiLocalHost : props.env.BASE_URL;

      const response = await axios.get(
        baseUrl +
          `v${props.env.API_VERSION}/${subId}/${password}/UserLogin?api-version=${props.env.API_VERSION}.0`
      );

      const newSubscriberDetails = new SubscriberDetails();
      newSubscriberDetails.subscriberID = response.data.subscriberDetailsResult.subscriberDetails.subscriberID;
      newSubscriberDetails.password = password;
      newSubscriberDetails.name = response.data.subscriberDetailsResult.subscriberDetails.name;
      newSubscriberDetails.accountManager = response.data.subscriberDetailsResult.subscriberDetails.account_Manager;
      newSubscriberDetails.email = response.data.subscriberDetailsResult.subscriberDetails.email;
      newSubscriberDetails.phoneNo = response.data.subscriberDetailsResult.subscriberDetails.phone_No;
      newSubscriberDetails.city = response.data.subscriberDetailsResult.subscriberDetails.city;
      newSubscriberDetails.address1 = response.data.subscriberDetailsResult.subscriberDetails.address1;
      newSubscriberDetails.address2 = response.data.subscriberDetailsResult.subscriberDetails.address2;
      newSubscriberDetails.company = response.data.subscriberDetailsResult.subscriberDetails.company;
      newSubscriberDetails.county = response.data.subscriberDetailsResult.subscriberDetails.county;
      newSubscriberDetails.postcode = response.data.subscriberDetailsResult.subscriberDetails.postcode;
      newSubscriberDetails.faxNo = response.data.subscriberDetailsResult.subscriberDetails.fax_No;
      newSubscriberDetails.directPhoneNo = response.data.subscriberDetailsResult.subscriberDetails.direct_Phone_No; 

      const newPackages = new Packages();
      const packageList = response.data.latestPackagesForSubscriberResult.packages || [];
      for (let i = 0; i < packageList.length; i++) {
        let packageDetails = new PackageDetails();
        packageDetails.productID = packageList[i].productID;
        packageDetails.productName = packageList[i].productName;
        packageDetails.packageDate = packageList[i].package_Date;
        packageDetails.packageFilename = packageList[i].package_Filename;
        newPackages.packageDetails.push(packageDetails);
      }

      const newMonthlyDownloadDates = new ListMonthlyDownloadDateResult();
      const monthlyDates = response.data.listMonthlyDownloadDateResult.monthlyDownloadDates || [];
      const monthlyDateCount = Math.min(config.numberOfMonthlyDownloadDates, monthlyDates.length);
      for (let i = 0; i < monthlyDateCount; i++) {
        let monthlyDownloadDate = new MonthlyDownloadDate();
        monthlyDownloadDate.downloadDate = monthlyDates[i].downloadDate;
        monthlyDownloadDate.productMonth = monthlyDates[i].productMonth;
        newMonthlyDownloadDates.monthlyDownloadDates.push(monthlyDownloadDate);
      }
      newMonthlyDownloadDates.success = response.data.listMonthlyDownloadDateResult.success;
      newMonthlyDownloadDates.error = response.data.listMonthlyDownloadDateResult.error;

      const newGuid = response.data.subscriberLoginResult.guid;
      const newLastLogin = response.data.latestLoginResult.lastLogin;

      setSubscriberDetails(newSubscriberDetails);
      setPackages(newPackages);
      setMonthlyDownloadDates(newMonthlyDownloadDates);
      setGuid(newGuid);
      setLastLogin(newLastLogin);
      setUserAuthenticated(true);

      props.user({
        userAuthenticated: true,
        subscriberDetails: newSubscriberDetails,
        guid: newGuid,
        packages: newPackages,
        monthlyDownloadDates: newMonthlyDownloadDates,
        lastLogin: newLastLogin,
        dataLoading: false
      });

      localStorage.setItem('USER_AUTHENTICATED', 'true');
      localStorage.setItem('SUBSCRIBER_DETAILS', JSON.stringify(newSubscriberDetails));
      localStorage.setItem('GUID', newGuid);
      localStorage.setItem('PACKAGES', JSON.stringify(newPackages));
      localStorage.setItem('MONTHLY_DOWNLOAD_DATES', JSON.stringify(newMonthlyDownloadDates));
      localStorage.setItem('LAST_LOGIN', JSON.stringify(newLastLogin));

      return { userAuthenticated: true };
    } catch (error) {
      return { userAuthenticated: false, error };
    } finally {
      setDataLoading(false);
    }
  }

  // Navigate on successful authentication
  useEffect(() => {
    if (userAuthenticated) {
      navigate("/home", { replace: true });
    }
  }, [userAuthenticated, navigate]);

  async function loginHandler(subId, password) {
    if (subId === '' || password === '') return;
    const result = await loginSubscriber(subId, password);
    if (!result?.userAuthenticated) {
      setLoginMessage(config.loginErrorMessage);
    } else {
      setLoginMessage('');
    }
    setSubId('');
    setPassword('');
  }

  // hide password string in browser tools
  function encodePassword(password) {
    return Buffer.from(password).toString('base64');
  }

  return (
    <div className="login-container">
      {dataLoading ? <Spinner /> :
        <div className="login-input-container">
          <div className="login-box">
            <form
              onSubmit={(e) => {
                e.preventDefault(); // Prevent page reload
                if (subId !== '' && password !== '') {
                  loginHandler(subId, password);
                }
              }}
            >
              <label>
                <p>Subscriber ID</p>
                <input
                  type="text"
                  onChange={e => setSubId(e.target.value)}
                />
              </label>
              <label>
                <p>Password</p>
                <input
                  type="password"
                  onChange={e => setPassword(encodePassword(e.target.value))}
                />
              </label>
              <button
                type="submit"
                disabled={subId === '' || password === ''}
              >
                Login
              </button>
            </form>
          </div>
          <span className="login-message">{loginMessage}</span>
        </div>
      }
    </div>

  );
}

export default Login;
